import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lightning, Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to set auth cookie
const setAuthCookie = (token) => {
  // Set cookie with appropriate settings
  document.cookie = `auth_token=${token}; path=/; max-age=${60 * 60 * 8}; SameSite=Lax`;
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = location.state?.from?.pathname || '/dashboard';
  const oidcReturn = searchParams.get('oidc_return');

  // If user is already logged in and there's an OIDC return, handle it
  useEffect(() => {
    if (user && token && oidcReturn) {
      // Set auth cookie for OIDC flow
      setAuthCookie(token);
      // Redirect back to OIDC authorize
      window.location.href = oidcReturn;
    }
  }, [user, token, oidcReturn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      // If there's an OIDC return URL, set cookie and redirect
      if (oidcReturn) {
        setAuthCookie(result.token);
        toast.success('Authenticated! Redirecting...');
        // Small delay to ensure cookie is set
        setTimeout(() => {
          window.location.href = oidcReturn;
        }, 100);
      } else {
        toast.success('Welcome to GETGRIDGATE!');
        navigate(from, { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-8">
            <img 
              src="/getgridgate-logo.png" 
              alt="GetGridGate" 
              className="w-40 h-40 object-contain"
            />
            <div>
              <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
                GETGRIDGATE
              </h1>
              <p className="text-base text-slate-500">Utility Operations Platform</p>
            </div>
          </div>

          {/* OIDC SSO Banner */}
          {oidcReturn && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <ShieldCheck size={24} className="text-blue-600" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Single Sign-On</p>
                <p className="text-blue-700">Sign in to continue to the application</p>
              </div>
            </div>
          )}

          <Card className="border-slate-200 shadow-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="font-display text-2xl">Sign in</CardTitle>
              <CardDescription>
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      {oidcReturn ? 'Authenticating...' : 'Signing in...'}
                    </span>
                  ) : (
                    oidcReturn ? 'Sign in & Continue' : 'Sign in'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 text-center">
                  Demo credentials:<br />
                  <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                    admin@getgridgate.com / Admin123!
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Hero Image */}
      <div 
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1759542877886-39d81e8f2eee?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-4xl font-bold mb-4">
            Powering Utility Excellence
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            Streamline operations, manage crews, track resources, and ensure compliance 
            with our comprehensive utility management platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
